import { Icon, IconButton, Text } from "@assetwatch/ui";
import { useState } from "react";
import type { WorkOrderFile } from "../../../queries/useWorkOrderComments";
import { formatDate } from "./WorkOrderDetailLayout";

/**
 * One Work Order attachment: a 4:3 image preview with the file name, uploader
 * and upload time, opening the attachments lightbox when activated.
 *
 * The open button covers the whole tile (the POC's tile is clickable
 * everywhere); the remove button sits beside it, above the caption, so no
 * interactive element is nested inside another.
 */
/* @ds-deviation: the DS Thumbnail is a fixed square (40–120 px) with no
   caption, remove action or lazy-loading slot, so this fluid 4:3 captioned,
   removable image tile is composed from tokens here — promote a captioned,
   removable, aspect-ratio tile variant into the design system (see the
   DS-DEVIATIONS.md row; related to the procedure photo tile row). */
export function WorkOrderAttachmentTile({
  file,
  canRemove,
  removePending = false,
  onOpen,
  onRemove,
  onImageError,
  openButtonRef,
}: {
  file: WorkOrderFile;
  /** True when the caller may remove this file and the Work Order allows changes. */
  canRemove: boolean;
  removePending?: boolean;
  onOpen: () => void;
  onRemove?: () => void;
  onImageError?: () => void;
  openButtonRef?: (element: HTMLButtonElement | null) => void;
}) {
  const [failedSrc, setFailedSrc] = useState<string | null>(null);
  const imageFailed = failedSrc === file.downloadUrl;

  return (
    <div className="relative min-w-0 overflow-hidden rounded-(--radius-lg) border border-outline-subtle bg-surface-container-low">
      <button
        aria-label={`Open ${file.fileName}`}
        className="flex w-full cursor-pointer flex-col border-none bg-transparent p-0 text-left outline-none focus-visible:ring-2 focus-visible:ring-button-focus-ring focus-visible:ring-inset"
        onClick={onOpen}
        ref={openButtonRef}
        type="button"
      >
        <span className="flex aspect-[4/3] w-full items-center justify-center overflow-hidden bg-surface-container">
          {imageFailed ? (
            <Icon aria-hidden name="image-regular" size="lg" />
          ) : (
            <img
              alt={file.fileName}
              className="size-full object-cover"
              decoding="async"
              loading="lazy"
              onError={() => {
                setFailedSrc(file.downloadUrl);
                onImageError?.();
              }}
              src={file.downloadUrl}
            />
          )}
        </span>
        <span
          className={`flex w-full min-w-0 flex-col gap-(--spacing-4) py-(--spacing-12) pl-(--spacing-12) ${canRemove ? "pr-(--spacing-40)" : "pr-(--spacing-12)"}`}
        >
          <Text as="span" size="sm" title={file.fileName} truncate>
            {file.fileName}
          </Text>
          <Text as="span" size="sm" tone="subtle" truncate>
            {file.author} · {formatDate(file.createdOn)}
          </Text>
        </span>
      </button>
      {canRemove ? (
        <div className="absolute right-(--spacing-4) bottom-(--spacing-12)">
          <IconButton
            aria-label={`Remove ${file.fileName}`}
            disabled={removePending}
            icon="x-close"
            onClick={onRemove}
            size="sm"
            variant="ghost"
          />
        </div>
      ) : null}
    </div>
  );
}
